/**
 * SocialProofFieldSelector
 *
 * Autonomous component for selecting social proof entities:
 * Reviews, Ratings, StudyCases, BigNumbers.
 *
 * - Loads all 4 entity types from Firestore independently
 * - Uses rc-tree for tree display grouped by category
 * - Supports path formats: @review[ID], @rating[ID], @studyCase[ID], @bigNumber[ID]
 * - Optimizes paths: deduplicates entity-level paths
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Typography, LinearProgress } from '@mui/material';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import type { Key } from 'rc-tree/lib/interface';

import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';

import type { SocialProofFieldSelectorProps, SocialProofTreeNode, LoadedSocialProofData } from './types';
import { socialProofToTreeNodes } from './socialProofToTreeNode';
import { treeNodeIdsToPaths, pathsToTreeNodeIds, deduplicatePaths } from './pathConverter';
import { pathsToJson } from './pathsToJson';

/**
 * SocialProofFieldSelector Component
 */
export const SocialProofFieldSelector: React.FC<SocialProofFieldSelectorProps> = ({
  projectId,
  value,
  onChange,
  height = 400,
  selectAll = false,
  onHydratedDataChange,
}) => {
  const [data, setData] = useState<LoadedSocialProofData>({
    reviews: [],
    ratings: [],
    studyCases: [],
    bigNumbers: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 4, currentItem: '' });

  // Load all social proof entities from Firestore
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadProgress({ loaded: 0, total: 4, currentItem: 'Loading social proof data...' });

        const reviewService = new ReviewService(projectId);
        const ratingService = new RatingService(projectId);
        const studyCaseService = new StudyCaseService(projectId);
        const bigNumberService = new BigNumberService(projectId);

        const [reviews, ratings, studyCases, bigNumbers] = await Promise.all([
          reviewService.list({ limit: 100 }),
          ratingService.list({ limit: 100 }),
          studyCaseService.list({ limit: 100 }),
          bigNumberService.list({ limit: 100 }),
        ]);

        if (isMounted) {
          setData({ reviews, ratings, studyCases, bigNumbers });
          const totalLoaded = reviews.length + ratings.length + studyCases.length + bigNumbers.length;
          setLoadProgress({
            loaded: totalLoaded,
            total: totalLoaded,
            currentItem: 'Complete',
          });
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load social proof data');
          console.error('Failed to load social proof data:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  const totalEntities = data.reviews.length + data.ratings.length + data.studyCases.length + data.bigNumbers.length;

  // Handle selectAll: auto-select all items after loading
  useEffect(() => {
    if (selectAll && totalEntities > 0 && value.length === 0 && !loading) {
      const allPaths: string[] = [
        ...data.reviews.map((r) => `@review[${r.id}]`),
        ...data.ratings.map((r) => `@rating[${r.id}]`),
        ...data.studyCases.map((sc) => `@studyCase[${sc.id}]`),
        ...data.bigNumbers.map((bn) => `@bigNumber[${bn.id}]`),
      ];
      onChange(allPaths);
    }
  }, [selectAll, totalEntities, value.length, loading, onChange, data]);

  // Generate hydrated data when value or data changes
  useEffect(() => {
    if (onHydratedDataChange && totalEntities > 0) {
      if (value.length > 0) {
        const hydratedData = pathsToJson(value, data);
        onHydratedDataChange(hydratedData);
      } else {
        onHydratedDataChange({
          reviews: [],
          ratings: [],
          studyCases: [],
          bigNumbers: [],
        });
      }
    }
  }, [value, data, totalEntities, onHydratedDataChange]);

  // Convert entities to tree data
  const treeData = useMemo<SocialProofTreeNode[]>(() => {
    return socialProofToTreeNodes(data.reviews, data.ratings, data.studyCases, data.bigNumbers);
  }, [data]);

  // Convert value (paths) to checkedKeys (node keys)
  const checkedKeys = useMemo<Key[]>(() => {
    if (!value || value.length === 0) return [];
    return pathsToTreeNodeIds(value, treeData);
  }, [value, treeData]);

  // Handle selection changes
  const handleCheck = (checkedKeysValue: Key[] | { checked: Key[]; halfChecked: Key[] }) => {
    const checkedArray = Array.isArray(checkedKeysValue)
      ? checkedKeysValue
      : checkedKeysValue.checked;

    const stringKeys = checkedArray.map((key) => String(key));

    // Convert node keys back to paths (only item nodes produce paths)
    const paths = treeNodeIdsToPaths(stringKeys, treeData);
    const dedupedPaths = deduplicatePaths(paths);

    onChange(dedupedPaths);
  };

  // Loading state
  if (loading) {
    const progressPercent =
      loadProgress.total > 0 ? Math.round((loadProgress.loaded / loadProgress.total) * 100) : 0;

    return (
      <Box
        data-testid="socialproof-picker-loading"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 2,
          px: 4,
        }}
      >
        <CircularProgress size={40} />

        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <LinearProgress
            variant={loadProgress.total > 0 ? 'determinate' : 'indeterminate'}
            value={progressPercent}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {loadProgress.currentItem || 'Loading social proof...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        data-testid="socialproof-picker-error"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography variant="body1" color="error">
          Error loading data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // Empty state
  if (totalEntities === 0) {
    return (
      <Box
        data-testid="socialproof-picker-empty"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No social proof data found (reviews, ratings, study cases, big numbers)
        </Typography>
      </Box>
    );
  }

  // Display tree
  return (
    <Box
      data-testid="socialproof-picker-tree"
      sx={{
        height,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Tree
        treeData={treeData}
        checkable
        checkedKeys={checkedKeys}
        onCheck={handleCheck}
        defaultExpandAll={true}
        showIcon={false}
      />
    </Box>
  );
};
